import { copyNow } from '../i18n/vtoCopy';
import type { LipDetection } from '../tracking/faceLandmarker';

export type ExposureClass = 'low' | 'balanced' | 'high';
export type ContrastClass = 'flat' | 'balanced' | 'harsh';
export type WhiteBalanceClass = 'cool' | 'neutral' | 'warm';

export interface LightingCalibrationTuning {
  skinStrengthScale: number;
  cheekOpacityScale: number;
  eyeOpacityScale: number;
  eyeSparkleScale: number;
  lipOpacityScale: number;
  lipFinishScale: number;
}

export interface LightingCalibrationReport {
  exposure: ExposureClass;
  contrast: ContrastClass;
  whiteBalance: WhiteBalanceClass;
  confidence: number;
  meanLuminance: number;
  contrastRange: number;
  redBlueBias: number;
  tuning: LightingCalibrationTuning;
  warnings: string[];
}

const FACE_OVAL = [
  10, 338, 297, 332, 284, 251, 389, 356, 454, 323,
  361, 288, 397, 365, 379, 378, 400, 377, 152, 148,
  176, 149, 150, 136, 172, 58, 132, 93, 234, 127,
  162, 21, 54, 103, 67, 109
];

// Tracciati separati per occhio sinistro e destro: un singolo array unito
// produce un poligono che attraversa il dorsale del naso, escludendo
// erroneamente pelle valida dal sample. Vanno passati a tracePath uno alla
// volta dentro lo stesso beginPath() per ottenere due "fori" indipendenti
// con fill('evenodd').
const EXCLUDE_LEFT_EYE = [
  33, 246, 161, 160, 159, 158, 157, 173, 133,
  155, 154, 153, 145, 144, 163, 7
];

const EXCLUDE_RIGHT_EYE = [
  263, 466, 388, 387, 386, 385, 384, 398, 362,
  382, 381, 380, 374, 373, 390, 249
];

const EXCLUDE_LIPS = [
  61, 185, 40, 39, 37, 0, 267, 269, 270, 409, 291,
  375, 321, 405, 314, 17, 84, 181, 91, 146
];

const EXCLUDE_BROWS = [
  70, 63, 105, 66, 107,
  336, 296, 334, 293, 300
];

const DEFAULT_TUNING: LightingCalibrationTuning = {
  skinStrengthScale: 1,
  cheekOpacityScale: 1,
  eyeOpacityScale: 1,
  eyeSparkleScale: 1,
  lipOpacityScale: 1,
  lipFinishScale: 1
};

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function tracePath(
  ctx: CanvasRenderingContext2D,
  detection: LipDetection,
  indices: readonly number[],
  width: number,
  height: number
): void {
  let started = false;
  for (const idx of indices) {
    const p = detection.landmarks[idx];
    if (!p) continue;
    const x = p.x * width;
    const y = p.y * height;
    if (!started) {
      ctx.moveTo(x, y);
      started = true;
    } else {
      ctx.lineTo(x, y);
    }
  }
  if (started) ctx.closePath();
}

function percentile(values: number[], p: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const index = clamp(Math.round((sorted.length - 1) * p), 0, sorted.length - 1);
  return sorted[index] ?? 0;
}

function sampleFacePhotometry(
  source: HTMLCanvasElement | ImageBitmap,
  detection: LipDetection,
  size = 96
): { meanLum: number; contrastRange: number; redBlueBias: number; samples: number } | null {
  const image = document.createElement('canvas');
  image.width = size;
  image.height = size;
  const ictx = image.getContext('2d', { willReadFrequently: true });
  if (!ictx) return null;
  ictx.drawImage(source, 0, 0, size, size);

  const mask = document.createElement('canvas');
  mask.width = size;
  mask.height = size;
  const mctx = mask.getContext('2d', { willReadFrequently: true });
  if (!mctx) return null;

  mctx.fillStyle = '#ffffff';
  mctx.beginPath();
  tracePath(mctx, detection, FACE_OVAL, size, size);
  tracePath(mctx, detection, EXCLUDE_LEFT_EYE, size, size);
  tracePath(mctx, detection, EXCLUDE_RIGHT_EYE, size, size);
  tracePath(mctx, detection, EXCLUDE_LIPS, size, size);
  // Vedi note in skinToneCalibration: 0.014 lasciava l'80% dei pixel
  // sopracciglio nel sample, abbassando meanLum e gonfiando contrastRange.
  for (const idx of EXCLUDE_BROWS) {
    const p = detection.landmarks[idx];
    if (!p) continue;
    const radius = size * 0.05;
    mctx.moveTo(p.x * size + radius, p.y * size);
    mctx.arc(p.x * size, p.y * size, radius, 0, Math.PI * 2);
  }
  mctx.fill('evenodd');

  const pixels = ictx.getImageData(0, 0, size, size).data;
  const maskPixels = mctx.getImageData(0, 0, size, size).data;
  const lums: number[] = [];
  let red = 0;
  let blue = 0;
  let totalLum = 0;
  let samples = 0;

  for (let i = 0; i < pixels.length; i += 4) {
    if ((maskPixels[i + 3] ?? 0) < 200) continue;
    const r = (pixels[i] ?? 0) / 255;
    const g = (pixels[i + 1] ?? 0) / 255;
    const b = (pixels[i + 2] ?? 0) / 255;
    const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    lums.push(lum);
    totalLum += lum;
    red += r;
    blue += b;
    samples++;
  }

  if (samples < 120) return null;
  const meanLum = totalLum / samples;
  const contrastRange = percentile(lums, 0.9) - percentile(lums, 0.1);
  const redBlueBias = (red - blue) / samples;
  return { meanLum, contrastRange, redBlueBias, samples };
}

export function analyzeLightingCalibration(
  source: HTMLCanvasElement | ImageBitmap,
  detection: LipDetection
): LightingCalibrationReport {
  const lcopy = copyNow().lighting;
  const sample = sampleFacePhotometry(source, detection);
  const tuning: LightingCalibrationTuning = { ...DEFAULT_TUNING };
  const warnings: string[] = [];

  if (!sample) {
    return {
      exposure: 'balanced',
      contrast: 'balanced',
      whiteBalance: 'neutral',
      confidence: 0,
      meanLuminance: 0,
      contrastRange: 0,
      redBlueBias: 0,
      tuning,
      warnings: [lcopy.unmeasurable]
    };
  }

  const exposure: ExposureClass =
    sample.meanLum < 0.42 ? 'low' :
    sample.meanLum > 0.72 ? 'high' :
    'balanced';
  const contrast: ContrastClass =
    sample.contrastRange < 0.18 ? 'flat' :
    sample.contrastRange > 0.42 ? 'harsh' :
    'balanced';
  const whiteBalance: WhiteBalanceClass =
    sample.redBlueBias > 0.095 ? 'warm' :
    sample.redBlueBias < 0.025 ? 'cool' :
    'neutral';

  if (exposure === 'low') {
    warnings.push(lcopy.lowExposure);
    tuning.skinStrengthScale *= 0.9;
    tuning.cheekOpacityScale *= 1.08;
    tuning.eyeOpacityScale *= 1.06;
    tuning.eyeSparkleScale *= 0.9;
    tuning.lipOpacityScale *= 1.05;
    tuning.lipFinishScale *= 0.94;
  } else if (exposure === 'high') {
    warnings.push(lcopy.highExposure);
    tuning.skinStrengthScale *= 0.88;
    tuning.cheekOpacityScale *= 0.88;
    tuning.eyeOpacityScale *= 0.9;
    tuning.eyeSparkleScale *= 0.82;
    tuning.lipOpacityScale *= 0.94;
    tuning.lipFinishScale *= 0.9;
  }

  if (contrast === 'flat') {
    tuning.cheekOpacityScale *= 1.04;
    tuning.eyeOpacityScale *= 1.04;
    tuning.lipOpacityScale *= 1.03;
  } else if (contrast === 'harsh') {
    warnings.push(lcopy.harshContrast);
    tuning.cheekOpacityScale *= 0.9;
    tuning.eyeOpacityScale *= 0.9;
    tuning.eyeSparkleScale *= 0.82;
    tuning.lipFinishScale *= 0.9;
  }

  if (whiteBalance === 'warm') {
    tuning.cheekOpacityScale *= 0.97;
    tuning.eyeOpacityScale *= 0.98;
    tuning.lipOpacityScale *= 0.99;
  } else if (whiteBalance === 'cool') {
    tuning.cheekOpacityScale *= 1.03;
    tuning.eyeOpacityScale *= 1.02;
    tuning.lipOpacityScale *= 1.01;
  }

  tuning.skinStrengthScale = clamp(tuning.skinStrengthScale, 0.82, 1.06);
  tuning.cheekOpacityScale = clamp(tuning.cheekOpacityScale, 0.78, 1.16);
  tuning.eyeOpacityScale = clamp(tuning.eyeOpacityScale, 0.78, 1.14);
  tuning.eyeSparkleScale = clamp(tuning.eyeSparkleScale, 0.72, 1.04);
  tuning.lipOpacityScale = clamp(tuning.lipOpacityScale, 0.88, 1.12);
  tuning.lipFinishScale = clamp(tuning.lipFinishScale, 0.78, 1.04);

  const confidence = clamp(sample.samples / (96 * 96 * 0.22), 0, 1);
  return {
    exposure,
    contrast,
    whiteBalance,
    confidence,
    meanLuminance: sample.meanLum,
    contrastRange: sample.contrastRange,
    redBlueBias: sample.redBlueBias,
    tuning,
    warnings
  };
}
